import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  User,
  ArrowRight,
  LayoutDashboard,
  LogOut,
  Linkedin,
  Instagram,
  Facebook,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import logo from "@/assets/logo.webp";
import MenuToggle from "@/components/MenuToggle";
import PlatformsNavDropdown from "@/components/PlatformsNavDropdown";

const NavigationHeader = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isToggling, setIsToggling] = useState(false);
  // Hover state for the sliding pill animation
  const [hoveredPath, setHoveredPath] = useState(null);

  const location = useLocation();
  const { isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();

  const isRadiantPage = location.pathname === "/terms" || location.pathname === "/privacy";

  const handleLogoClick = () => {
    if (location.pathname !== "/") return;
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "Resources", path: "/resources" },
    { name: "Platforms", path: "/platform", dropdown: true },
    { name: "About", path: "/about" },
    { name: "Careers", path: "/careers" },
    { name: "Contact", path: "/contact" },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleMenuToggle = (newState) => {
    if (isToggling) return;
    setIsToggling(true);
    setMobileMenuOpen(newState);
    setTimeout(() => {
      setIsToggling(false);
    }, 1200);
  };

  return (
    <>
      {/* Floating Logo (Scrolled) */}
      <AnimatePresence>
        {isScrolled && !mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, x: -20, scale: 0.8 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: -20, scale: 0.8 }}
            transition={{ type: "spring", stiffness: 200, damping: 25, mass: 0.5 }}
            className="fixed top-4 left-4 z-50 md:top-5 md:left-8 lg:left-12 xl:left-23">
            <Link to="/" onClick={handleLogoClick} className="block">
              <div className="flex items-center justify-center rounded-xl border border-white/45 bg-[#C8CED6]/70 px-2.5 py-1.5 shadow-[0_0_20px_rgba(255,255,255,0.7)] backdrop-blur-lg md:block md:bg-[#C8CED6]/70 md:p-4 md:shadow-[0_0_20px_rgba(255,255,255,0.7)] md:backdrop-blur-lg">
                <img
                  src={logo}
                  alt="VetAssist"
                  className="h-8 w-auto object-contain md:h-12"
                  loading="eager"
                  decoding="async"
                />
              </div>
            </Link>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Header */}
      <header
        className={`pointer-events-none fixed flex items-center transition-all duration-300 ${
          mobileMenuOpen ? "z-200" : "z-90"
        } ${isScrolled ? "top-4 right-4 w-auto md:top-5 md:right-8 lg:right-12" : "top-0 left-0 w-full"}`}>
        <div
          className={`pointer-events-auto flex items-center transition-colors duration-500 ${
            mobileMenuOpen
              ? "gap-2 overflow-visible border-transparent bg-transparent"
              : isScrolled
                ? "overflow-hidden gap-1 rounded-xl border border-white/45 bg-[#C8CED6]/70 px-2 py-1 shadow-[0_0_20px_rgba(255,255,255,0.7)] backdrop-blur-lg md:gap-2 md:px-3 md:py-2"
                : "w-full justify-between overflow-visible bg-transparent px-4 py-4 md:px-8 md:py-5 lg:px-12 lg:py-6 xl:px-24"
          }`}>
          <AnimatePresence mode="popLayout">
            {!isScrolled && !mobileMenuOpen && (
              <motion.div
                initial={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20, width: 0 }}
                transition={{ duration: 0.2 }}
                className="flex shrink-0 items-center gap-2">
                <Link to="/" onClick={handleLogoClick} className="flex items-center">
                  <img
                    src={logo}
                    alt="VetAssist"
                    loading="eager"
                    decoding="async"
                    className={`h-10 drop-shadow-[0_0_20px_rgba(255,255,255,0.7)] transition-all duration-300 sm:h-12 md:h-14 lg:h-16 ${
                      isRadiantPage ? "brightness-110" : ""
                    }`}
                  />
                </Link>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Desktop Nav with Sliding Pill Animation */}
          <AnimatePresence mode="popLayout">
            {!isScrolled && !mobileMenuOpen && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.1 }}
                className="hidden flex-1 items-center justify-center px-8 lg:flex">
                <nav
                  className="relative flex items-center gap-2 lg:gap-4 xl:gap-8"
                  onMouseLeave={() => setHoveredPath(null)}>
                  {navLinks.map((link) =>
                    link.dropdown ? (
                      <PlatformsNavDropdown
                        key={link.path}
                        link={link}
                        hoveredPath={hoveredPath}
                        onHoverChange={setHoveredPath}
                      />
                    ) : (
                      <Link
                        key={link.path}
                        to={link.path}
                        onMouseEnter={() => setHoveredPath(link.path)}
                        className={`relative px-3 py-2 text-sm font-semibold whitespace-nowrap transition-colors duration-300 lg:px-4 lg:py-2.5 xl:px-6 xl:py-3 xl:text-lg ${
                          isScrolled
                            ? "text-[#23355B] hover:text-[#23355B]/80"
                            : "text-[#23355B] hover:text-[#23355B]/80"
                        }`}>
                        {/* The Sliding Highlight Pill */}
                        {hoveredPath === link.path && (
                          <motion.div
                            layoutId="nav-hover-pill"
                            className={`absolute inset-0 z-0 rounded-xl ${
                              isScrolled ? "bg-[#C5D3F5]" : "bg-[#23355B]/10"
                            }`}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{
                              type: "spring",
                              stiffness: 400,
                              damping: 30,
                            }}
                          />
                        )}

                        {/* Link Text */}
                        <span className="relative z-10">{link.name}</span>
                      </Link>
                    )
                  )}
                </nav>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Right Actions */}
          <div
            className={`ml-auto flex shrink-0 items-center gap-1.5 md:gap-3 ${!isScrolled ? "lg:mr-4 xl:mr-10" : ""}`}>
            <div
              className={`flex items-center gap-1.5 transition-opacity duration-300 md:gap-3 ${
                mobileMenuOpen ? "pointer-events-none opacity-0" : "opacity-100"
              }`}>
              <Button
                onClick={() => navigate("/demo")}
                className={`cursor-pointer text-xs font-bold whitespace-nowrap shadow-[0_0_20px_rgba(255,255,255,0.7)] transition-all sm:text-sm lg:text-base ${
                  isScrolled
                    ? "h-9 rounded-lg bg-[#2546A1] px-2.5 py-1.5 text-[11px] text-white hover:bg-[#1d3782] md:h-11 md:rounded-xl md:px-5 md:py-2.5 md:text-sm lg:h-12 lg:px-7 lg:py-3 lg:text-base"
                    : `h-10 rounded-lg bg-[#2546A1] px-4 py-2 text-white hover:bg-[#1d3782] md:h-11 md:rounded-xl md:px-5 md:py-2.5 lg:h-12 lg:px-7 lg:py-3 ${
                        isRadiantPage ? "brightness-110" : ""
                      }`
                }`}>
                <span className="hidden font-semibold min-[390px]:inline">BOOK A DEMO</span>
                <span className="inline font-semibold min-[390px]:hidden">DEMO</span>
              </Button>

              <Link
                to={isAuthenticated ? "/dashboard" : "/admin"}
                aria-label={isAuthenticated ? "Open dashboard" : "Open sign in"}
                title={isAuthenticated ? "Dashboard" : "Sign in"}
                className={isScrolled ? "max-[389px]:hidden" : "lg:hidden"}>
                <motion.button
                  type="button"
                  aria-label={isAuthenticated ? "Open dashboard" : "Open sign in"}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="group flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg p-0 transition-colors hover:bg-gray-100/50 md:h-auto md:w-auto md:rounded-xl md:p-2.5">
                  {isAuthenticated ? (
                    <LayoutDashboard
                      className={`h-5 w-5 transition-colors md:h-6 md:w-6 ${
                        isScrolled ? "text-[#23355B]" : "text-[#23355B]"
                      }`}
                    />
                  ) : (
                    <User
                      className={`h-5 w-5 transition-colors md:h-6 md:w-6 ${
                        isScrolled ? "text-gray-700" : "text-[#23355B]"
                      }`}
                    />
                  )}
                </motion.button>
              </Link>
            </div>

            <div
              className={`relative z-250 flex h-9 w-9 items-center justify-center transition-opacity duration-300 md:h-auto md:w-auto ${
                mobileMenuOpen ? "pointer-events-none opacity-0" : ""
              } ${isScrolled ? "" : "lg:hidden"}`}>
              <MenuToggle
                isOpen={mobileMenuOpen}
                toggle={() => handleMenuToggle(!mobileMenuOpen)}
                lineColor={
                  mobileMenuOpen ? "bg-[#23355B]" : isScrolled ? "bg-gray-700" : "bg-[#23355B]"
                }
              />
            </div>
          </div>
        </div>
      </header>

      {/* Full Screen Menu Sheet - LIGHT MODE WITH GRADIENT */}
      <Sheet open={mobileMenuOpen} onOpenChange={handleMenuToggle}>
        <SheetContent
          side="full"
          className="h-dvh! w-screen! max-w-none! overflow-hidden border-none bg-gradient-to-br from-[#eef2ff] via-[#f8fafc] to-[#f0fdf4] p-0! outline-none">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="relative isolate flex h-full flex-col overflow-hidden">
            
            {/* Animated Gradient Orbs - layering over the background gradient for depth */}
            <div className="pointer-events-none absolute inset-0 overflow-hidden">
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 0.35, scale: 1 }}
                transition={{ duration: 1.5, ease: "easeOut" }}
                className="absolute -top-40 -right-40 h-96 w-96 rounded-full bg-blue-300 blur-[120px]"
              />
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 0.15, scale: 1 }}
                transition={{ duration: 1.5, delay: 0.2, ease: "easeOut" }}
                className="absolute top-1/2 -left-40 h-96 w-96 rounded-full bg-teal-300 blur-[120px]"
              />
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 0.25, scale: 1 }}
                transition={{ duration: 1.5, delay: 0.4, ease: "easeOut" }}
                className="absolute right-1/4 -bottom-40 h-96 w-96 rounded-full bg-purple-300 blur-[120px]"
              />
            </div>

            {/* CLOSE BUTTON */}
            <div className="absolute top-6 right-8 z-260">
              <MenuToggle
                isOpen={true}
                toggle={() => handleMenuToggle(false)}
                lineColor="bg-[#23355B]"
              />
            </div>

            {/* --- BACKGROUND ICON --- */}
            <div className="pointer-events-none absolute top-1/2 left-0 z-0 hidden h-[60vh] w-full -translate-y-1/2 items-center justify-center lg:flex lg:h-[80vh] lg:w-1/2">
              <motion.img
                src={logo}
                alt="Background Icon"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{
                  opacity: [0.06, 0.15, 0.06],
                  scale: [0.98, 1, 0.98],
                }}
                transition={{
                  duration: 6,
                  ease: "easeInOut",
                  repeat: Infinity,
                  repeatType: "reverse",
                }}
                className="pointer-events-none h-full w-auto object-contain drop-shadow-[0_25px_35px_rgba(0,0,0,0.06)] grayscale select-none"
                style={{ mixBlendMode: "multiply" }}
              />
            </div>

            {/* --- CONTENT CONTAINER --- */}
            <div className="relative z-10 ml-auto flex h-full w-full flex-1 flex-col justify-between overflow-hidden px-8 pt-12 pb-6 lg:w-3/5 lg:px-16 xl:w-1/2">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3, duration: 0.8 }}
                className="flex w-full shrink-0 flex-col items-start">
                
                {/* Auth Row - LIGHT MODE STYLES */}
                <motion.div
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4, duration: 0.6 }}
                  className="mb-5 flex flex-wrap items-center gap-3 lg:mb-8">
                  {isAuthenticated ? (
                    <>
                      <Link
                        to="/dashboard"
                        onClick={() => handleMenuToggle(false)}
                        className="inline-block">
                        <button className="group relative flex cursor-pointer items-center gap-3 overflow-hidden rounded-2xl border border-blue-400/40 bg-blue-50 px-6 py-3.5 shadow-sm transition-all duration-300 hover:border-blue-400 hover:bg-blue-100">
                          <div className="absolute inset-0 -translate-x-full bg-linear-to-r from-transparent via-white/40 to-transparent transition-transform duration-700 group-hover:translate-x-full" />
                          <LayoutDashboard className="h-5 w-5 text-[#2546A1] transition-transform group-hover:scale-110" />
                          <span className="relative text-sm font-bold tracking-wide text-[#1A3A5C]">
                            Dashboard
                          </span>
                        </button>
                      </Link>
                      <button
                        onClick={() => {
                          handleMenuToggle(false);
                          logout();
                        }}
                        className="group relative flex cursor-pointer items-center gap-3 overflow-hidden rounded-2xl border border-red-400/40 bg-red-50 px-6 py-3.5 shadow-sm transition-all duration-300 hover:border-red-400 hover:bg-red-100">
                        <div className="absolute inset-0 -translate-x-full bg-linear-to-r from-transparent via-white/40 to-transparent transition-transform duration-700 group-hover:translate-x-full" />
                        <LogOut className="h-5 w-5 text-red-500 transition-transform group-hover:scale-110" />
                        <span className="relative text-sm font-bold tracking-wide text-[#1A3A5C]">
                          Logout
                        </span>
                      </button>
                    </>
                  ) : (
                    <Link
                      to="/admin"
                      onClick={() => handleMenuToggle(false)}
                      className="inline-block">
                      <button className="group relative flex cursor-pointer items-center gap-3 overflow-hidden rounded-2xl border border-blue-400/40 bg-blue-50 px-6 py-3.5 shadow-sm transition-all duration-300 hover:border-blue-400 hover:bg-blue-100">
                        <div className="absolute inset-0 -translate-x-full bg-linear-to-r from-transparent via-white/40 to-transparent transition-transform duration-700 group-hover:translate-x-full" />
                        <User className="h-5 w-5 fill-[#2546A1] text-[#2546A1] transition-transform group-hover:scale-110" />
                        <span className="relative text-sm font-bold tracking-wide text-[#1A3A5C]">
                          Sign in to VetAssist
                        </span>
                      </button>
                    </Link>
                  )}
                </motion.div>

                {/* Main Links */}
                <div className="w-full">
                  {navLinks.map((link, idx) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      onClick={() => handleMenuToggle(false)}
                      className="block">
                      <motion.div
                        initial={{ opacity: 0, x: -30 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{
                          delay: 0.5 + idx * 0.1,
                          duration: 0.6,
                          ease: [0.22, 1, 0.36, 1],
                        }}
                        className="group relative flex w-full cursor-pointer items-center overflow-hidden rounded-2xl px-4 py-3 transition-all duration-300 hover:bg-[#e2e8f0] sm:py-4 lg:px-6 lg:py-4">
                        <div className="absolute inset-0 bg-linear-to-r from-blue-600/0 via-blue-500/5 to-purple-600/0 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                        <span className="relative text-3xl font-extrabold tracking-tight text-[#0f172a] transition-all duration-300 group-hover:translate-x-2 group-hover:text-[#2546A1] sm:text-4xl lg:text-5xl">
                          {link.name}
                        </span>
                        <ArrowRight className="absolute right-6 h-6 w-6 translate-x-2 text-[#0f172a]/0 transition-all duration-300 group-hover:translate-x-0 group-hover:text-[#2546A1]/70 lg:h-8 lg:w-8" />
                        <div className="absolute right-0 bottom-0 left-0 h-px bg-linear-to-r from-transparent via-[#94a3b8]/30 to-transparent" />
                      </motion.div>
                    </Link>
                  ))}
                </div>

                {/* Secondary Links */}
                <div className="mt-2 flex flex-wrap gap-2 px-4 lg:px-6">
                  {[
                    {
                      name: "EWS Hosting Products",
                      url: "https://ewshosting.net/",
                      external: true,
                    },
                    { name: "Privacy", path: "/privacy", external: false },
                    { name: "Terms", path: "/terms", external: false },
                  ].map((link) =>
                    link.external ? (
                      <a
                        key={link.name}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs font-medium text-[#64748b] transition-colors hover:text-[#0f172a]">
                        {link.name}
                      </a>
                    ) : (
                      <Link
                        key={link.name}
                        to={link.path}
                        onClick={() => handleMenuToggle(false)}
                        className="text-xs font-medium text-[#64748b] transition-colors hover:text-[#0f172a]">
                        {link.name}
                      </Link>
                    )
                  )}
                </div>

                {/* Social Row - LIGHT MODE STYLES */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.9, duration: 0.6 }}
                  className="mt-4 flex flex-col gap-3 lg:mt-6">
                  <div className="flex flex-wrap items-center gap-3">
                    <a
                      href="https://www.linkedin.com/showcase/vet-assist-ph"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group inline-flex cursor-pointer items-center gap-2 rounded-xl border border-slate-300/60 bg-white px-5 py-3 shadow-sm transition-all duration-300 hover:border-blue-400 hover:bg-blue-50">
                      <Linkedin className="h-4 w-4 text-[#64748b] transition-all duration-300 group-hover:translate-x-1 group-hover:text-[#2546A1]" />
                      <span className="text-sm font-bold text-[#0f172a] transition-colors group-hover:text-[#2546A1]">
                        LinkedIn
                      </span>
                      <ArrowRight className="h-4 w-4 text-[#64748b] transition-all duration-300 group-hover:translate-x-1 group-hover:text-[#2546A1]" />
                    </a>

                    <a
                      href="https://www.facebook.com/VetAssistApp/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group inline-flex cursor-pointer items-center gap-2 rounded-xl border border-slate-300/60 bg-white px-5 py-3 shadow-sm transition-all duration-300 hover:border-blue-400 hover:bg-blue-50">
                      <Facebook className="h-4 w-4 text-[#64748b] transition-all duration-300 group-hover:translate-x-1 group-hover:text-[#2546A1]" />
                      <span className="text-sm font-bold text-[#0f172a] transition-colors group-hover:text-[#2546A1]">
                        Facebook
                      </span>
                      <ArrowRight className="h-4 w-4 text-[#64748b] transition-all duration-300 group-hover:translate-x-1 group-hover:text-[#2546A1]" />
                    </a>

                    <a
                      href="https://www.instagram.com/vet_assist_app/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group inline-flex cursor-pointer items-center gap-2 rounded-xl border border-slate-300/60 bg-white px-5 py-3 shadow-sm transition-all duration-300 hover:border-purple-400 hover:bg-purple-50">
                      <Instagram className="h-4 w-4 text-[#64748b] transition-all duration-300 group-hover:translate-x-1 group-hover:text-purple-600" />
                      <span className="text-sm font-bold text-[#0f172a] transition-colors group-hover:text-purple-600">
                        Instagram
                      </span>
                      <ArrowRight className="h-4 w-4 text-[#64748b] transition-all duration-300 group-hover:translate-x-1 group-hover:text-purple-600" />
                    </a>

                    <a
                      href="https://www.tiktok.com/@vetassistapp"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group inline-flex cursor-pointer items-center gap-2 rounded-xl border border-slate-300/60 bg-white px-5 py-3 shadow-sm transition-all duration-300 hover:border-slate-400 hover:bg-slate-100">
                      <svg 
                        className="h-4 w-4 text-[#64748b] transition-all duration-300 group-hover:translate-x-1 group-hover:text-[#0f172a]" 
                        viewBox="0 0 24 24" 
                        fill="currentColor"
                      >
                        <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93v6.16c0 4.34-3.7 8.06-8.21 7.94-2.62-.07-5.07-1.29-6.77-3.28-1.7-1.99-2.45-4.65-2.03-7.25.45-2.66 2.03-5.05 4.32-6.49 1.71-1.08 3.77-1.6 5.85-1.49v4.01c-.96-.12-1.93-.07-2.87.17-.76.2-1.48.54-2.09 1-1.02.77-1.67 1.91-1.8 3.15-.18 1.52.4 3.05 1.57 4.06.86.74 1.96 1.15 3.08 1.09 1.98-.08 3.64-1.72 3.68-3.71L12.525.02z" />
                      </svg>
                      <span className="text-sm font-bold text-[#0f172a] transition-colors group-hover:text-[#0f172a]">
                        TikTok
                      </span>
                      <ArrowRight className="h-4 w-4 text-[#64748b] transition-all duration-300 group-hover:translate-x-1 group-hover:text-[#0f172a]" />
                    </a>
                  </div>
                </motion.div>
              </motion.div>

              {/* BOTTOM SECTION: Book Demo */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.0, duration: 0.6 }}
                className="mt-4 flex flex-col justify-end">
                <motion.div className="w-full">
                  <button
                    onClick={() => {
                      handleMenuToggle(false);
                      navigate("/demo");
                    }}
                    className="group relative w-full cursor-pointer overflow-hidden rounded-2xl bg-linear-to-r from-blue-600 to-purple-600 px-8 py-4 shadow-2xl shadow-blue-600/30 transition-all duration-300 hover:from-blue-500 hover:to-purple-500 hover:shadow-blue-500/50">
                    <div className="absolute inset-0 translate-x-[-200%] bg-linear-to-r from-transparent via-white/20 to-transparent transition-transform duration-1000 group-hover:translate-x-[200%]" />
                    <div className="relative flex items-center justify-between">
                      <span className="text-lg font-bold tracking-wide text-white lg:text-xl">
                        Book a Demo
                      </span>
                      <div className="flex items-center gap-2">
                        <span className="hidden text-sm font-medium text-white/80 sm:block">
                          Get Started
                        </span>
                        <ArrowRight className="h-5 w-5 text-white transition-transform duration-300 group-hover:translate-x-2 lg:h-6 lg:w-6" />
                      </div>
                    </div>
                  </button>
                </motion.div>
              </motion.div>
            </div>
          </motion.div>
        </SheetContent>
      </Sheet>


    </>
  );
};

export default NavigationHeader;